import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Button,
  CircularProgress,
  Popover,
  Divider,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  FileDownloadOutlined as DownloadIcon,
  FilterAltOutlined as FilterAltOutlinedIcon
} from '@mui/icons-material';
import VehicleListTable from 'src/components/vehicles/VehicleListTable';
import NewVehicle from 'src/components/vehicles/NewVehicle';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import API_URL from 'src/api/apiConstants';
import { BASEAPI } from 'src/api/api';
import moment from 'moment';
import { DATE_FORMAT } from 'src/utils/constants';
import { setErrorInfo } from 'src/redux/modalSlice';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import ExportColumnSelector from 'src/common/ExportColumnSelector';
import { getAllVehicleGroupList } from 'src/redux/groupSlice';
import { useHistory, useLocation } from 'react-router-dom';
import TableColumnVisibilityMenu, { useTableColumnPreferences } from 'src/common/TableColumnVisibilityMenu';
import { vehicleTableHeaders } from 'src/utils/tableHeaders';
import ListRefreshButton from 'src/common/ListRefreshButton';
import { getVehicleDetails } from 'src/redux/vehicleSlice';
import ColumnSearch from 'src/common/ColumnSearch';

const Vehicles = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const initialParams = new URLSearchParams(location.search);
  const { allVehicleGroups = [] } = useSelector((state) => state.groups);
  const {
    isAdmin,
    siteFilter,
    siteOptions
  } = useAdminSiteFilter(initialParams.get('site') || 'all');
  const vehicleColumnPreferences = useTableColumnPreferences('vehicles', vehicleTableHeaders);
  const initialVehicleSearchValues = {
    registration_num: initialParams.get('registration_num') || initialParams.get('search') || '',
    asset_code: initialParams.get('asset_code') || '',
    site_name: initialParams.get('site_name') || ''
  };
  const initialVehicleSearchFilters = Object.fromEntries(
    Object.entries(initialVehicleSearchValues).filter(([, value]) => value)
  );

  const [openModal, setOpenModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchFilters, setSearchFilters] = useState(initialVehicleSearchFilters);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showExportColumns, setShowExportColumns] = useState(false);
  const [pendingExportColumns, setPendingExportColumns] = useState([]);
  const [appliedSite, setAppliedSite] = useState(initialParams.get('site') || 'all');
  const [appliedGroup, setAppliedGroup] = useState(initialParams.get('group') || 'all');
  const [draftSite, setDraftSite] = useState(siteFilter || 'all');
  const [draftGroup, setDraftGroup] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAllVehicleGroupList());
    }
  }, [dispatch, isAdmin]);

  const groupOptions = useMemo(
    () => [{ id: 'all', name: 'All Groups' }, ...allVehicleGroups],
    [allVehicleGroups]
  );

  const isFilterPopoverOpen = Boolean(filterAnchorEl);

  const updateQuery = (patch) => {
    const params = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([key, value]) => {
      if (value && value !== 'all') params.set(key, value);
      else params.delete(key);
    });
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  const handleSearchFilters = (values) => {
    setSearchFilters(values);
    updateQuery({
      search: '',
      registration_num: values.registration_num,
      asset_code: values.asset_code,
      site_name: values.site_name
    });
  };

  const clearSearchFilters = () => {
    setSearchFilters({});
    updateQuery({
      search: '',
      registration_num: '',
      asset_code: '',
      site_name: ''
    });
  };
  const openFilterPopover = (e) => {
    setDraftSite(appliedSite || 'all');
    setDraftGroup(appliedGroup || 'all');
    setFilterAnchorEl(e.currentTarget);
  };

  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleClearFilterDrafts = () => {
    setDraftSite('all');
    setDraftGroup('all');
  };

  const handleApplyFilters = () => {
    setAppliedSite(draftSite || 'all');
    setAppliedGroup(draftGroup || 'all');
    updateQuery({ site: draftSite, group: draftGroup });
    closeFilterPopover();
  };

  const handleExport = (columns) => {
    setShowExportConfirm(false);
    setLoading(true);
    BASEAPI.get(`${API_URL.vehicleList}?export=true&columns=${encodeURIComponent(columns.join(','))}`, {
      responseType: 'blob',
      timeout: 20000
    })
      .then((res) => {
        const fileName = `vehicles_${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
        saveAs(res.data, fileName);
      })
      .catch(() => {
        dispatch(setErrorInfo({ errorMsg: 'Error downloading report' }));
      })
      .finally(() => setLoading(false));
  };

  const handleEditVehicle = async (vehicleId) => {
    try {
      const vehicleDetails = await dispatch(getVehicleDetails(vehicleId)).unwrap();
      setEditingVehicle(vehicleDetails);
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.error_message || 'Unable to load vehicle details.'
      }));
    }
  };

  const vehicleExportColumns = [
    { id: 'registration_num', label: 'Registration Number' },
    { id: 'active', label: 'Status' },
    { id: 'model', label: 'Vehicle Model' },
    { id: 'site_name', label: 'Site Name' },
    { id: 'asset_code', label: 'Asset Code' },
    { id: 'type', label: 'Type' },
    { id: 'group_name', label: 'Vehicle Group' },
    { id: 'standard_mileage', label: 'Standard Mileage' },
  ];

  return (
    <>
      <Helmet>
        <title>Vehicles | Retrack FMS</title>
      </Helmet>
      <Box
        className="transaction-style-page transaction-style-duplicate-header"
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* ---------- TOOLBAR ---------- */}
          <Stack
            className="vehicle-list-toolbar"
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            spacing={3}
            sx={{
              py: 2,
              mb: 2,
              flexShrink: 0,
              width: '100%'
            }}
          >
            <Stack
              direction="row"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                width: { xs: '100%', lg: 'auto' },
                flexShrink: 0
              }}
            >
              <ColumnSearch
                fields={[
                  { key: 'registration_num', label: 'Registration Number', defaultChecked: true },
                  { key: 'asset_code', label: 'Asset Code' },
                  { key: 'site_name', label: 'Site Name' }
                ]}
                values={searchFilters}
                onApply={handleSearchFilters}
                onClear={clearSearchFilters}
                sx={{ width: { xs: '100%', sm: 320, lg: 340 } }}
                containerSx={{ width: { xs: '100%', sm: 'auto' } }}
              />
              <ListRefreshButton onRefresh={() => setRefreshKey((value) => value + 1)} />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                gap: 2,
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                width: { xs: '100%', lg: 'auto' },
                flex: { lg: '0 0 auto' },
                ml: { lg: 'auto' }
              }}
            >

              {isAdmin && (
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterAltOutlinedIcon />}
                    onClick={openFilterPopover}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      minHeight: 40,
                      borderRadius: 2,
                      width: {
                        xs: '100%',
                        sm: 140,
                        md: 160,
                        xl: 180
                      },
                      whiteSpace: 'nowrap',
                      mr: 0
                    }}
                  >
                    Filters
                  </Button>
                  <Popover
                    open={isFilterPopoverOpen}
                    anchorEl={filterAnchorEl}
                    onClose={closeFilterPopover}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left'
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        width: { xs: '92vw', sm: 360 },
                        p: 2,
                        borderRadius: 2
                      }
                    }}
                  >
                    <Stack spacing={1.5}>
                      <FilterSelectInput
                        label="Site"
                        name="site_filter"
                        value={draftSite}
                        data={siteOptions}
                        onInputChange={setDraftSite}
                        sx={{ width: '100%', mr: 0 }}
                      />
                      <FilterSelectInput
                        label="Group"
                        name="group_filter"
                        value={draftGroup}
                        data={groupOptions}
                        onInputChange={setDraftGroup}
                        sx={{ width: '100%', mr: 0 }}
                      />
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilterDrafts}
                        sx={{ textTransform: 'none' }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleApplyFilters}
                        sx={{ textTransform: 'none' }}
                      >
                        Apply
                      </Button>
                    </Stack>
                  </Popover>
                </Box>
              )}

              <TableColumnVisibilityMenu
                columns={vehicleTableHeaders}
                hiddenColumnIds={vehicleColumnPreferences.hiddenColumnIds}
                onToggleColumn={vehicleColumnPreferences.onToggleColumn}
                buttonLabel="Columns"
              />

              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={() => setShowExportColumns(true)}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {isLoading ? 'Exporting...' : 'Export'}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  backgroundColor: theme.palette.primary.main,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Add Vehicle
              </Button>
            </Box>
          </Stack>

          {/* ---------- CONTENT ---------- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <VehicleListTable
              refreshData={refreshKey}
              searchFilters={searchFilters}
              siteFilter={appliedSite}
              groupFilter={appliedGroup}
              columnPreferences={vehicleColumnPreferences}
              onEditVehicle={handleEditVehicle}
            />
          </Box>

          <NewVehicle
            open={openModal}
            handleClose={() => setOpenModal(false)}
            onSuccess={() => setRefreshKey((value) => value + 1)}
          />
          <NewVehicle
            open={Boolean(editingVehicle)}
            initialValues={editingVehicle}
            handleClose={() => setEditingVehicle(null)}
            onSuccess={() => setRefreshKey((value) => value + 1)}
          />

          <ConfirmationDialog
            open={showExportConfirm}
            info="Are you sure you want to export vehicles report?"
            actionName="Export"
            onClose={() => {
              setShowExportConfirm(false);
              setPendingExportColumns([]);
            }}
            onSubmit={() => handleExport(pendingExportColumns)}
          />
          <ExportColumnSelector
            open={showExportColumns}
            columns={vehicleExportColumns}
            onClose={() => setShowExportColumns(false)}
            onConfirm={(columns) => {
              setShowExportColumns(false);
              setPendingExportColumns(columns);
              setShowExportConfirm(true);
            }}
          />
        </Container>
      </Box>
    </>
  );
};

Vehicles.propTypes = {};

export default Vehicles;
